import React from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// Card , generic container with white surface, hairline border, soft shadow.
//
//   <Card>{...}</Card>
//   <Card padding="sm|md|lg|none">{...}</Card>
//
// RowCard , small clickable row used for chapters, sidebar items, options.
//   <RowCard tone="idle|active|locked|modified"
//            leading={<Num/Icon>}
//            trailing={<Check/Lock/StatusTag>}
//            onClick={fn}>
//     {label}
//   </RowCard>
// ─────────────────────────────────────────────────────────────────────────────

const PAD = {
  none: '',
  sm:   'p-3',
  md:   'p-5',
  lg:   'p-6',
}

export function Card({ padding = 'lg', as: As = 'div', className = '', children, ...rest }) {
  return (
    <As
      className={
        'bg-white border border-[#e7e5df] rounded-[14px] shadow-[0_1px_2px_rgba(20,20,16,.03)] ' +
        (PAD[padding] || '') +
        (className ? ' ' + className : '')
      }
      {...rest}
    >
      {children}
    </As>
  )
}

const ROW_TONE = {
  idle:
    'bg-white border border-[#e7e5df] hover:bg-[#f5f4f0] hover:border-[#d6d3cb] cursor-pointer',
  active:
    'bg-[#fff1e8] border border-[rgba(232,93,31,0.30)] ' +
    'shadow-[0_1px_2px_rgba(232,93,31,.04),0_6px_16px_-12px_rgba(232,93,31,.35)] ' +
    'hover:bg-[#ffe2cf] cursor-pointer',
  locked:
    'bg-[#fff1e8] border border-[rgba(232,93,31,0.30)] ' +
    'shadow-[0_1px_2px_rgba(232,93,31,.04),0_6px_16px_-12px_rgba(232,93,31,.35)] cursor-default',
  modified:
    'bg-[#fff1e8] border border-[rgba(232,93,31,0.30)] ' +
    'shadow-[inset_3px_0_0_0_rgba(184,117,20,.65),0_1px_2px_rgba(232,93,31,.04),0_6px_16px_-12px_rgba(232,93,31,.35)] ' +
    'hover:bg-[#ffe2cf] cursor-pointer',
}

export function RowCard({
  tone = 'idle',
  leading,
  trailing,
  onClick,
  className = '',
  as: As = 'div',
  children,
}) {
  const palette = ROW_TONE[tone] || ROW_TONE.idle
  const isClickable = onClick && (tone !== 'locked')
  return (
    <As
      onClick={isClickable ? onClick : undefined}
      className={
        'flex items-center gap-3 px-3 py-2.5 rounded-[12px] transition-colors ' +
        palette + (className ? ' ' + className : '')
      }
    >
      {leading}
      <span
        className={
          'flex-1 min-w-0 text-[13.5px] leading-[1.32] truncate ' +
          (tone === 'idle' ? 'text-[#1a1816] font-medium' : 'text-[#7a2b08] font-semibold')
        }
      >
        {children}
      </span>
      {trailing}
    </As>
  )
}

// Helper: the number badge used inside chapter rows (`1`, `2`, …).
export function NumberBadge({ children, tone = 'idle' }) {
  const palette =
    tone === 'idle'
      ? 'bg-[#f5f4f0] text-[#6b6862]'
      : 'bg-[#e85d1f] text-white shadow-[0_2px_6px_rgba(232,93,31,.35)]'
  return (
    <span
      className={
        'grid place-items-center w-7 h-7 rounded-md font-mono text-[12px] font-semibold flex-shrink-0 ' +
        palette
      }
    >
      {children}
    </span>
  )
}

// Helper: the orange icon-chip used for the locked Deckblatt row.
export function IconChip({ icon: Icon, size = 28, iconSize = 14 }) {
  return (
    <span
      className="grid place-items-center rounded-md bg-[#e85d1f] text-white flex-shrink-0 shadow-[0_2px_6px_rgba(232,93,31,.35)]"
      style={{ width: size, height: size }}
    >
      <Icon size={iconSize}/>
    </span>
  )
}
